import i18next from 'i18next';
import React from 'react';

import {
  tableColumnAmount,
  tableColumnCreatedDate,
  tableColumnEmail,
  tableColumnSupporterFullName,
} from 'src/components/common/tableColumns';
import { UiButton } from 'src/components/UI/Button';
import { UiHoverTooltip } from 'src/components/UI/HoverTooltip';
import { UiOuterLink } from 'src/components/UI/OuterLink';
import { UiTableColumnItemType, UiTableFilterType } from 'src/components/UI/table/UiTable.helpers';
import { UiTextTooltip } from 'src/components/UI/TextTooltip';
import { Enums } from 'src/models/enums';
import { PaymentTypeEnum } from 'src/pages/Administration/Campaigns/Transactions/types';
import { dispatchOverlaySliceShow } from 'src/store/overlay/helpers';
import { CDate } from 'src/utils/CDate';
import { commonFormatCurrencyWithType } from 'src/utils/currency';
import { SafeAnyType } from 'src/utils/safeAny';
import { safeTruncate } from 'src/utils/safeTruncate';
import { SortDirectionEnum } from 'src/utils/types';
import { getUrlFilters } from 'src/utils/url';

const statusCodes = Enums.campaigns.StatusCode;
const displayingStatusCodes = Enums.campaigns.DisplayingStatusCode;

const HEADER_MAX_LENGTH = 30;

const t = (key: string) => i18next.t(`common.${key}`);

export const MyCampaignsFilterStatus = (header?: string): UiTableFilterType & { header?: string } => ({
  type: 'lookup',
  header,
  propertyName: 'DisplayingStatusCode',
  lookup: [
    {
      name: t('campaignStatuses.draft'),
      value: displayingStatusCodes.Draft,
    },
    {
      name: t('campaignStatuses.pending'),
      value: displayingStatusCodes.Pending,
    },
    {
      name: t('campaignStatuses.live'),
      value: displayingStatusCodes.Live,
    },
    {
      name: t('campaignStatuses.ended'),
      value: displayingStatusCodes.Ended,
    },
    {
      name: t('campaignStatuses.scheduled'),
      value: displayingStatusCodes.Scheduled,
    },
  ],
});

export const MyCampaignsFilterTypeIds = (header?: string): UiTableFilterType & { header?: string } => {
  const campaignTypeCodes = Enums.campaigns.FundraisingActivityTypeCode;

  return {
    type: 'intersect',
    propertyName: 'TypeIDs',
    header,
    lookup: [
      {
        name: t('campaignComponents.athon'),
        value: campaignTypeCodes.Pledge,
      },
      {
        name: t('campaignComponents.auction'),
        value: campaignTypeCodes.Auction,
      },
      {
        name: t('campaignComponents.crowdfunding'),
        value: campaignTypeCodes.Funding,
      },
      {
        name: t('campaignComponents.raffle'),
        value: campaignTypeCodes.Raffle,
      },
      {
        name: t('campaignComponents.sale'),
        value: campaignTypeCodes.Sale,
      },
      {
        name: t('campaignComponents.sweepstakes'),
        value: campaignTypeCodes.Sweepstakes,
      },
      {
        name: t('campaignComponents.ticketing'),
        value: campaignTypeCodes.Ticketing,
      },
    ],
  };
};

export const MyCampaignsFilterFeatureIds = (header?: string): UiTableFilterType & { header?: string } => {
  const campaignFeatures = Enums.campaigns.FeatureFilter;
  return {
    type: 'intersect',
    propertyName: 'CampaignFeatureFilters',
    header,
    lookup: [
      {
        name: t('campaignFeatures.livestream'),
        value: campaignFeatures.LiveStreaming,
      },
      {
        name: t('campaignFeatures.p2p'),
        value: campaignFeatures.PeerToPeerFunctionality,
      },
    ],
  };
};

export const commonCampaignsColumns: Record<
  | 'ID'
  | 'StartDate'
  | 'EndDate'
  | 'Name'
  | 'BenefitingOrganizationID'
  | 'RunningOrganizationID'
  | 'StatusCode'
  | 'IsApprovedByAdmin'
  | 'SubDate'
  | 'TotalContributed'
  | 'OrganizationName'
  | 'VerificationSubDate'
  | 'ApproveReason'
  | 'EIN'
  | 'StripeOnBehalfAccountID'
  | 'FullName'
  | 'Email'
  | 'RoleCode'
  | 'StatusCodeInvite'
  | 'PaymentID'
  | 'CampaignID'
  | 'CreatedDate'
  | 'SupporterFullName'
  | 'SupporterEmail'
  | 'CampaignName'
  | 'SplitPercentage'
  | 'PaymentFee'
  | 'ContributionPaymentAmount'
  | 'PaymentTypeID'
  | 'OrgStatusID'
  | 'TransactionStatusID'
  | 'Amount'
  | 'FirstName'
  | 'LastName',
  (title: string, otherParams?: SafeAnyType) => UiTableColumnItemType
> = {
  ID: (
    title,
    { renderValue = 'ID', flagged = false, flagProperty = 'AdminFlag' } = {
      renderValue: 'ID',
      flagged: false,
      flagProperty: 'AdminFlag',
    }
  ) => ({
    title,
    sortPropertyName: 'ID',
    className: 'text-left id',
    filter: {
      type: 'integer',
      propertyName: 'ID',
    },
    renderValue,
    flagged,
    flagProperty,
  }),
  StartDate: (title, { defaultSort } = { defaultSort: false }) => ({
    title,
    sortPropertyName: 'StartDate',
    className: 'text-center date',
    filter: {
      type: 'date',
      propertyName: 'StartDate',
    },
    defaultSortDirection: defaultSort ? SortDirectionEnum.DESC : undefined,
    renderValue: ({ StartDate }) => CDate.formatStringToDate(StartDate),
  }),
  EndDate: (title) => ({
    title,
    sortPropertyName: 'EndDate',
    className: 'text-center date',
    filter: {
      type: 'date',
      propertyName: 'EndDate',
    },
    renderValue: ({ EndDate }) => CDate.formatStringToDate(EndDate),
  }),
  Name: (title, justText = false) => ({
    title,
    sortPropertyName: 'Name',
    className: 'text-left',
    filter: {
      type: 'text',
      propertyName: 'Name',
    },
    renderValue: ({ Name, ID }) => {
      const renderTooltip = Name && Name.length > HEADER_MAX_LENGTH;
      let shortName = Name;

      if (justText || renderTooltip) {
        shortName = safeTruncate(Name, HEADER_MAX_LENGTH);
      }

      return (
        <UiHoverTooltip text={Name} render={renderTooltip}>
          {justText ? (
            shortName
          ) : (
            <UiButton
              tag="a"
              onClick={() => {
                dispatchOverlaySliceShow({ id: ID, type: 'campaign' });
              }}
            >
              {Name === shortName ? shortName : `${shortName}...`}
            </UiButton>
          )}
        </UiHoverTooltip>
      );
    },
  }),
  BenefitingOrganizationID: (title, url) => ({
    title,
    sortPropertyName: 'BenefitingOrganizationID',
    className: 'text-left beneficiary-id',
    filter: {
      type: 'integer',
      propertyName: 'BenefitingOrganizationID',
    },
    renderValue: ({ BenefitingOrganizationID, BenefitingOrganizationName }) =>
      BenefitingOrganizationID && (
        <UiOuterLink
          href={getUrlFilters(url, [
            {
              Type: 'integer',
              Column: 'ID',
              Value: BenefitingOrganizationID,
            },
          ])}
        >
          {BenefitingOrganizationName ? (
            <UiTextTooltip showHover text={BenefitingOrganizationName}>
              {BenefitingOrganizationID}
            </UiTextTooltip>
          ) : (
            BenefitingOrganizationID
          )}
        </UiOuterLink>
      ),
  }),
  RunningOrganizationID: (title, url) => ({
    title,
    sortPropertyName: 'RunningOrganizationID',
    className: 'text-left organization-id',
    filter: {
      type: 'integer',
      propertyName: 'RunningOrganizationID',
    },
    renderValue: ({ RunningOrganizationID, RunningOrganizationName }) =>
      RunningOrganizationID && (
        <UiOuterLink
          href={getUrlFilters(url, [
            {
              Type: 'integer',
              Column: 'ID',
              Value: RunningOrganizationID,
            },
          ])}
        >
          {RunningOrganizationName ? (
            <UiTextTooltip showHover text={RunningOrganizationName}>
              {RunningOrganizationID}
            </UiTextTooltip>
          ) : (
            RunningOrganizationID
          )}
        </UiOuterLink>
      ),
  }),
  StatusCode: (title) => ({
    title,
    sortPropertyName: 'StatusCode',
    className: 'text-center status',
    renderValue: 'Status',
    filter: {
      type: 'lookup',
      propertyName: 'StatusCode',
      lookup: [
        { name: t('campaignStatuses.draft'), value: statusCodes.Draft },
        { name: t('campaignStatuses.submissionProcessing'), value: statusCodes.SubmissionProcessing },
        { name: t('campaignStatuses.approved'), value: statusCodes.Approved },
        { name: t('campaignStatuses.returned'), value: statusCodes.Returned },
        { name: t('campaignStatuses.started'), value: statusCodes.Started },
        { name: t('campaignStatuses.frozen'), value: statusCodes.Frozen },
        { name: t('campaignStatuses.ended'), value: statusCodes.Ended },
        { name: t('campaignStatuses.processing'), value: statusCodes.Processing },
        { name: t('campaignStatuses.completed'), value: statusCodes.Completed },
        { name: t('campaignStatuses.failed'), value: statusCodes.Failed },
        { name: t('campaignStatuses.declined'), value: statusCodes.Declined },
        { name: t('campaignStatuses.funded'), value: statusCodes.Funded },
        { name: t('campaignStatuses.cancelled'), value: statusCodes.Cancelled },
      ],
      selected: [
        statusCodes.Draft,
        statusCodes.Approved,
        statusCodes.Returned,
        statusCodes.Started,
        statusCodes.Frozen,
        statusCodes.Ended,
        statusCodes.Processing,
      ],
    },
  }),
  IsApprovedByAdmin: (title) => ({
    title,
    sortPropertyName: 'IsApprovedByAdmin',
    className: 'text-center',
    filter: {
      type: 'boolean',
      propertyName: 'IsApprovedByAdmin',
      lookup: [
        { name: t('yes'), value: true },
        { name: t('no'), value: false },
      ],
      selected: [],
    },
    renderValue: ({ IsApprovedByAdmin }) => t(IsApprovedByAdmin ? 'yes' : 'no') as string,
  }),
  SubDate: (title, raw = false) => ({
    title,
    sortPropertyName: 'SubDate',
    className: 'text-center date',
    filter: {
      type: 'date',
      propertyName: 'SubDate',
    },
    defaultSortDirection: SortDirectionEnum.DESC,
    renderValue: raw ? 'SubDate' : ({ SubDate }) => CDate.formatStringToDate(SubDate),
  }),
  TotalContributed: (title) => ({
    title,
    sortPropertyName: 'TotalContributed',
    className: 'text-right amount',
    filter: {
      type: 'numeric',
      propertyName: 'TotalContributed',
    },
    renderValue: ({ TotalContributed, CurrencyType }) => (
      <>{commonFormatCurrencyWithType(TotalContributed, CurrencyType)}</>
    ),
  }),
  OrganizationName: (title, isText = false) => ({
    title,
    className: 'text-left',
    sortPropertyName: 'OrganizationName',
    filter: {
      type: 'text',
      propertyName: 'OrganizationName',
    },
    renderValue: ({ OrganizationName, HomeUrl }) =>
      isText ? OrganizationName : <UiOuterLink href={HomeUrl}>{OrganizationName}</UiOuterLink>,
  }),
  VerificationSubDate: (title) => ({
    title,
    className: 'text-center date',
    sortPropertyName: 'VerificationSubDate',
    filter: {
      type: 'date',
      propertyName: 'VerificationSubDate',
    },
    renderValue: 'VerificationSubDate',
  }),
  ApproveReason: (title) => ({
    title,
    className: 'text-center',
    sortPropertyName: 'ApproveReason',
    filter: {
      type: 'lookup',
      propertyName: 'ApproveReason',
      lookup: [
        { name: t('verificationMethods.stripeIdentity'), value: 1 },
        { name: t('verificationMethods.stripeFunding'), value: 5 },
        { name: t('verificationMethods.guideStar'), value: 2 },
        { name: t('verificationMethods.auto'), value: 3 },
        { name: t('verificationMethods.manual'), value: 4 },
        { name: t('verificationMethods.causeIq'), value: 6 },
      ],
      selected: [],
    },
    renderValue: 'ApproveReasonFormatted',
  }),
  EIN: (title) => ({
    title,
    className: 'left',
    sortPropertyName: 'EIN',
    filter: {
      type: 'text',
      propertyName: 'EIN',
    },
    renderValue: 'EIN',
  }),
  StripeOnBehalfAccountID: (title, visible = true) => ({
    title,
    className: 'left',
    sortPropertyName: 'StripeOnBehalfAccountID',
    filter: {
      type: 'text',
      propertyName: 'StripeOnBehalfAccountID',
    },
    renderValue: 'StripeOnBehalfAccountID',
    visible,
  }),
  FullName: (title, { defaultSort, useFilter } = { defaultSort: undefined, useFilter: true }) => ({
    title,
    sortPropertyName: 'FullName',
    defaultSortDirection: defaultSort,
    filter: useFilter
      ? {
          type: 'text',
          propertyName: 'FullName',
        }
      : undefined,
    className: 'text-left',
    renderValue: ({ FullName }) => <UiTextTooltip>{FullName}</UiTextTooltip>,
  }),
  Email: tableColumnEmail,
  RoleCode: (title, enableAdvocates = false) => {
    const { Administrator, Staff, Advocate } = Enums.organizations.StaffRoleCode;

    const roleLookup: {
      name: string;
      value: (typeof Enums.campaigns.PaymentProcessingTypeCode)[keyof typeof Enums.campaigns.PaymentProcessingTypeCode];
    }[] = [
      { name: t('roles.administrator'), value: Administrator },
      { name: t('roles.staff'), value: Staff },
    ];

    if (enableAdvocates) {
      roleLookup.push({ name: t('roles.advocate'), value: Advocate });
    }

    return {
      title,
      sortPropertyName: 'RoleCode',
      filter: {
        type: 'lookup',
        lookup: roleLookup,
        selected: [],
        propertyName: 'RoleCode',
      },
      className: 'text-center',
      renderValue: 'Role',
    };
  },
  StatusCodeInvite: (title) => ({
    title,
    sortPropertyName: 'StatusCode',
    filter: {
      type: 'lookup',
      lookup: [
        {
          name: t('invitationStatus.invited'),
          value: Enums.organizations.StaffInvitationStatusCode.Invited,
        },
        {
          name: t('invitationStatus.accepted'),
          value: Enums.organizations.StaffInvitationStatusCode.Accepted,
        },
      ],
      selected: [],
      propertyName: 'StatusCode',
    },
    className: 'text-center',
    renderValue: 'Status',
  }),
  PaymentID: (title) => ({
    title,
    className: 'text-left id',
    sortPropertyName: 'PaymentID',
    renderValue: 'PaymentID',
    filter: {
      type: 'integer',
      propertyName: 'PaymentID',
    },
  }),
  CampaignID: (title) => ({
    title,
    className: 'text-left id',
    sortPropertyName: 'CampaignID',
    renderValue: 'CampaignID',
    filter: {
      type: 'integer',
      propertyName: 'CampaignID',
    },
  }),
  CreatedDate: tableColumnCreatedDate,
  SupporterFullName: tableColumnSupporterFullName,
  SupporterEmail: (title) => ({
    title,
    className: 'text-left',
    sortPropertyName: 'SupporterEmail',
    renderValue: 'SupporterEmail',
    filter: {
      type: 'text',
      propertyName: 'SupporterEmail',
    },
  }),
  CampaignName: (title, { useFilter, urlName = 'CampaignUrl' } = { useFilter: true }) => ({
    title,
    className: 'text-left name',
    sortPropertyName: 'CampaignName',
    renderValue: ({ [urlName]: url, CampaignName }) => (
      <UiTextTooltip text={CampaignName}>
        <UiOuterLink href={url}>{CampaignName}</UiOuterLink>
      </UiTextTooltip>
    ),
    filter: useFilter
      ? {
          type: 'text',
          propertyName: 'CampaignName',
        }
      : undefined,
  }),
  SplitPercentage: (title, visible = true) => ({
    title,
    sortPropertyName: 'SplitPercentage',
    className: 'text-left split',
    filter: {
      type: 'integer',
      propertyName: 'SplitPercentage',
    },
    visible,
    renderValue: 'SplitPercentageString',
  }),
  PaymentFee: (title) => ({
    title,
    className: 'text-right amount',
    sortPropertyName: 'PaymentFee',
    renderValue: ({ PaymentFee, CurrencyType }) => commonFormatCurrencyWithType(PaymentFee, CurrencyType),
  }),
  ContributionPaymentAmount: (title) => ({
    title,
    className: 'text-right amount',
    sortPropertyName: 'ContributionPaymentAmount',
    renderValue: 'ContributionAmountString',
  }),
  PaymentTypeID: (title) => ({
    title,
    className: 'text-center',
    sortPropertyName: 'DisplayingPaymentTypeCode',
    renderValue: 'PaymentType',
    filter: {
      type: 'lookup',
      propertyName: 'DisplayingPaymentTypeCode',
      lookup: [
        { name: t(`campaignPaymentTypes.card`), value: PaymentTypeEnum.card },
        { name: t(`campaignPaymentTypes.payPal`), value: PaymentTypeEnum.payPal },
        { name: t(`campaignPaymentTypes.check`), value: PaymentTypeEnum.check },
        { name: t(`campaignPaymentTypes.cash`), value: PaymentTypeEnum.cash },
        { name: t(`campaignPaymentTypes.upi`), value: PaymentTypeEnum.upi },
        { name: t(`campaignPaymentTypes.netBanking`), value: PaymentTypeEnum.netBanking },
      ],
    },
  }),
  OrgStatusID: (title) => {
    const tHelper = (statusName: string) => t(`organizationStatuses.${statusName}`);

    const { Draft, PendingApproval, PendingVerification, Frozen, VerificationSubmitted, Approved, Declined } =
      Enums.organizations.StatusCode;

    return {
      title,
      className: 'text-center status',
      sortPropertyName: 'StatusID',
      filter: {
        type: 'lookup',
        propertyName: 'StatusID',
        lookup: [
          { name: tHelper('draft'), value: Draft },
          {
            name: tHelper('pendingApproval'),
            value: PendingApproval,
          },
          {
            name: tHelper('pendingVerification'),
            value: PendingVerification,
          },
          { name: tHelper('frozen'), value: Frozen },
          {
            name: tHelper('verificationSubmitted'),
            value: VerificationSubmitted,
          },
          { name: tHelper('approved'), value: Approved },
          { name: tHelper('declined'), value: Declined },
        ],
        selected: [],
      },
      renderValue: 'StatusName',
    };
  },
  TransactionStatusID: (title) => {
    const tHelper = (statusName: string) => t(`transactionStatuses.${statusName}`);

    const paymentStatusCode = Enums.contributions.PaymentStatusCode;

    return {
      title,
      className: 'text-center',
      sortPropertyName: 'StatusID',
      renderValue: 'Status',
      filter: {
        type: 'lookup',
        propertyName: 'StatusID',
        lookup: [
          { name: tHelper('pending'), value: paymentStatusCode.Pending },
          { name: tHelper('submitted'), value: paymentStatusCode.Submitted },
          { name: tHelper('paid'), value: paymentStatusCode.Paid },
          { name: tHelper('declined'), value: paymentStatusCode.Declined },
          { name: tHelper('voided'), value: paymentStatusCode.Voided },
          { name: tHelper('cancelled'), value: paymentStatusCode.Cancelled },
          { name: tHelper('free'), value: paymentStatusCode.Free },
          { name: tHelper('actionRequired'), value: paymentStatusCode.AuthenticationRequired },
          { name: tHelper('processing'), value: paymentStatusCode.Recovery },
          { name: tHelper('failed'), value: paymentStatusCode.Failed },
        ],
      },
    };
  },
  Amount: (title, customStringPropValue = false) => tableColumnAmount(title, { customStringPropValue }),
  FirstName: (title) => ({
    title,
    sortPropertyName: 'FirstName',
    filter: {
      type: 'text',
      propertyName: 'FirstName',
    },
    className: 'text-left',
    renderValue: 'FirstName',
  }),
  LastName: (title) => ({
    title,
    sortPropertyName: 'LastName',
    filter: {
      type: 'text',
      propertyName: 'LastName',
    },
    className: 'text-left',
    renderValue: 'LastName',
  }),
};

export const commonCampaignsActions: Record<
  | 'enableApprove'
  | 'canReturn'
  | 'canCancel'
  | 'canFreeze'
  | 'canDecline'
  | 'allowFreeze'
  | 'allowUnfreeze'
  | 'canArchive',
  (d: { StatusCode: number; IsApprovedByAdmin: boolean }) => boolean
> = {
  enableApprove: ({ StatusCode, IsApprovedByAdmin }): boolean =>
    (!IsApprovedByAdmin && StatusCode === statusCodes.Started) || StatusCode === statusCodes.Approved,
  canReturn: ({ StatusCode, IsApprovedByAdmin }) =>
    [statusCodes.Started, statusCodes.Frozen, statusCodes.Approved].includes(StatusCode as SafeAnyType) &&
    !IsApprovedByAdmin,
  canCancel: ({ StatusCode }) =>
    [statusCodes.Started, statusCodes.Draft, statusCodes.Approved].includes(StatusCode as SafeAnyType),
  allowFreeze: ({ StatusCode }) => StatusCode !== statusCodes.Frozen && StatusCode !== statusCodes.SubmissionProcessing,
  allowUnfreeze: ({ StatusCode }) => StatusCode === statusCodes.Frozen,
  canFreeze: ({ StatusCode }) => StatusCode === statusCodes.Started,
  canDecline: ({ StatusCode }) => StatusCode === statusCodes.Started,
  canArchive: ({ StatusCode }) => StatusCode !== statusCodes.SubmissionProcessing,
};
